import React, { useEffect, useState } from 'react';
import { Card, List, Space, Typography, Tag, Descriptions, Modal } from 'antd';
import { Wrench, Eye, CheckCircle, XCircle } from 'lucide-react';
import { useCrewStore } from '../stores/useCrewStore';
import { apiService } from '../services/api';
import type { Tool } from '../types';

const { Title, Text, Paragraph } = Typography;

const ToolsPage: React.FC = () => {
  const { tools, setTools } = useCrewStore();
  const [loading, setLoading] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    setLoading(true);
    try {
      const data = await apiService.getTools();
      setTools(data);
    } catch (error) {
      console.error('Failed to load tools:', error);
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (tool: Tool) => {
    setSelectedTool(tool);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Wrench className="inline-block mr-2" size={24} /> Tools
        </Title>
        <Text type="secondary">View tools currently available in the system</Text>
      </div>

      {tools.length === 0 && !loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Wrench size={64} className="text-gray-300" />
            <Title level={4} style={{ marginTop: 16 }}>No tools available</Title>
            <Text type="secondary">There are currently no tools available in the system</Text>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          dataSource={tools}
          loading={loading}
          renderItem={(tool) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => handleViewDetails(tool)}
                style={{ cursor: 'pointer' }}
              >
                <Card.Meta
                  title={
                    <Space>
                      {tool.name}
                      {tool.enabled ? (
                        <CheckCircle size={16} className="text-green-500 dark:text-green-400" />
                      ) : (
                        <XCircle size={16} className="text-red-500 dark:text-red-400" />
                      )}
                    </Space>
                  }
                  description={
                    <Text ellipsis style={{ maxWidth: '100%' }}>
                      {tool.description}
                    </Text>
                  }
                />
                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Type: </Text>
                      <Tag color="cyan">{tool.type}</Tag>
                    </div>
                    <div>
                      <Text type="secondary">Status: </Text>
                      {tool.enabled ? (
                        <Tag color="green">Enabled</Tag>
                      ) : (
                        <Tag color="red">Disabled</Tag>
                      )}
                    </div>
                    {tool.parameters && Object.keys(tool.parameters).length > 0 && (
                      <div>
                        <Text type="secondary">
                          {Object.keys(tool.parameters).length} parameter(s)
                        </Text>
                      </div>
                    )}
                  </Space>
                </div>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Text type="secondary">
                    <Eye size={14} className="inline-block mr-1" /> Click to view details
                  </Text>
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title="Tool Details"
        open={!!selectedTool}
        onCancel={() => setSelectedTool(null)}
        footer={null}
        width={700}
      >
        {selectedTool && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Name">{selectedTool.name}</Descriptions.Item>
            <Descriptions.Item label="Description">{selectedTool.description}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color="cyan">{selectedTool.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {selectedTool.enabled ? (
                <Tag color="green" icon={<CheckCircle size={12} />}>Enabled</Tag>
              ) : (
                <Tag color="red" icon={<XCircle size={12} />}>Disabled</Tag>
              )}
            </Descriptions.Item>
            {selectedTool.parameters && Object.keys(selectedTool.parameters).length > 0 && (
              <Descriptions.Item label="Parameters">
                <Paragraph>
                  <pre style={{
                    background: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(selectedTool.parameters, null, 2)}
                  </pre>
                </Paragraph>
              </Descriptions.Item>
            )}
            {selectedTool.created_at && (
              <Descriptions.Item label="Created At">
                {new Date(selectedTool.created_at).toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ToolsPage;
